/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef } from 'react';
import { APIEndpoint } from "../../../utils/axios";
import { formatDateByTimezone, storageUtil } from '../../../utils/commonFunctions';
import { toolTipInfo } from '../../../utils/itemSel';
import { useTranslation } from 'react-i18next';
import usePermissions from '../../../utils/userPermissions';
import { HOST_SERVER } from '../../../utils/configs';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { TermsOfUseComponent } from './termsOfUseComponent';

const MAX_LOGO_SIZE = 250 * 1024; 

const toastConfig = { position: 'top-center', hideProgressBar: true, pauseOnHover: true, draggable: false, autoClose: 2000 };
const showToast = (message, isError = false) => {
  toast.dismiss();
  const style = isError
    ? { backgroundColor: '#d9534f', color: '#fff', borderRadius: '8px', fontSize: '1rem' }
    : { backgroundColor: '#4cae4c', color: '#fff', borderRadius: '8px', fontSize: '1rem' };
  isError ? toast.error(message, { ...toastConfig, style }) : toast.success(message, { ...toastConfig, style });
};

/* ─── SVG icons ─── */
const IconAccount = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>;
const IconPOS   = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/></svg>;
const IconUplift= () => <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.56 3.91c.59.59.59 1.54 0 2.12l-3.89 3.89 2.12 9.19-1.41 1.41-3.88-7.43-3.9 3.9.36 2.47-1.07 1.06-1.76-3.18-3.19-1.77 1.06-1.06 2.47.36 3.87-3.87-7.43-3.88 1.41-1.41 9.19 2.12 3.89-3.89c.56-.58 1.56-.58 2.16 0z"/></svg>;
const IconBell  = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>;
const IconCloud = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"></path></svg>;


const InfoTip = ({ content }) => (
  <span className="cfg-info-tip" title={content} tabIndex={0} aria-label={content}>i
    <span className="cfg-tip-pop">{content}</span>
  </span>
);


const MiniToggle = ({ name, value, onChange, disabled }) => (
  <div className="cfg-toggle-wrap">
    <span className={`cfg-toggle-label${value ? ' on' : ''}`}>{value ? 'ON' : 'OFF'}</span>
    <div
      className={`cfg-toggle-pill${value ? ' on' : ''}${disabled ? ' disabled' : ''}`}
      onClick={() => !disabled && onChange(name)}
      role="switch"
      aria-checked={value}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !disabled) onChange(name); }}
    >
      <span className="cfg-toggle-thumb" />
    </div>
  </div>
);


const SettingRow = ({ label, desc, tooltip, children }) => (
  <div className="cfg-setting-row">
    <div className="cfg-setting-meta">
      <div className="cfg-setting-label">
        {label}
        {tooltip && <InfoTip content={tooltip} />}
      </div>
      {desc && <div className="cfg-setting-desc">{desc}</div>}
    </div>
    <div className="cfg-setting-control">{children}</div>
  </div>
);


const SettingsCard = ({ icon, title, subtitle, children }) => (
  <div className="cfg-card">
    <div className="cfg-card-header">
      <div className="cfg-card-icon">{icon}</div>
      <div>
        <div className="cfg-card-title">{title}</div>
        <div className="cfg-card-sub">{subtitle}</div>
      </div>
    </div>
    <div className="cfg-card-body">{children}</div>
  </div>
);

const LogoUploadModal = ({ onClose, onSaved }) => {
  const [dragOver, setDragOver]   = useState(false);
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null);
  const [error, setError]         = useState('');
  const [saving, setSaving]       = useState(false);
  const inputRef                  = useRef(null);

  React.useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);
  const validate = (f) => {
    if (!f) return;
    if (f.type !== 'image/png') { setError('Only PNG files are accepted.'); return; }
    if (f.size > MAX_LOGO_SIZE)  { setError(`File exceeds ${MAX_LOGO_SIZE / 1024} KB limit.`); return; }
    setError('');
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); validate(e.dataTransfer.files[0]); };
  const onPick = (e) => validate(e.target.files[0]);
  const removeFile = () => { setFile(null); setPreview(null); setError(''); };

  const onSave = async () => {
    if (!file) { setError('Please choose a PNG file first.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await APIEndpoint.put('portal/logo', fd);
      showToast('Logo updated successfully');
      onSaved();
      onClose();
    } catch (err) {
      const msg = (() => { try { return JSON.parse(err.message)?.data?.error; } catch { return null; } })();
      setError(msg || 'Upload failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cfg-modal-backdrop" onClick={onClose}>
      <div className="cfg-modal" onClick={e => e.stopPropagation()}>
        <div className="cfg-modal-header">
          <span className="cfg-modal-title">Upload Portal Logo</span>
          <button type="button" className="cfg-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="cfg-modal-body">

          <div
            className={`cfg-dropzone${dragOver ? ' dragover' : ''}${preview ? ' has-file' : ''}`}
            onClick={() => !preview && inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            <input ref={inputRef} type="file" accept="image/png" style={{ display: 'none' }} onChange={onPick} />

            {preview ? (
              <div className="cfg-dropzone-preview">
                <img src={preview} alt="Logo preview" />
              </div>
            ) : (
              <>
                <div className="cfg-dropzone-icon"><IconCloud /></div>
                <div className="cfg-dropzone-title">
                  Drag &amp; drop your logo here, or{' '}
                  <span
                    className="cfg-dropzone-browse"
                    role="button"
                    tabIndex={0}
                    onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        inputRef.current?.click();
                      }
                    }}
                  >
                    browse
                  </span>
                </div>
                <div className="cfg-dropzone-sub">PNG · max {MAX_LOGO_SIZE / 1024} KB · recommended height 64px</div>
              </>
            )}
          </div>

          {file && (
            <div className="cfg-file-row">
              <div className="cfg-file-thumb">
                <img src={preview} alt="" />
              </div>
              <div className="cfg-file-meta">
                <div className="cfg-file-name">{file.name}</div>
                <div className="cfg-file-size">{(file.size / 1024).toFixed(1)} KB</div>
              </div>
              <button className="cfg-file-remove" onClick={removeFile}>×</button>
            </div>
          )}

          {error && <div className="cfg-modal-error">{error}</div>}
        </div>

       <div className="cfg-modal-footer">
        <button className="cfg-modal-btn cfg-modal-btn--cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          className="cfg-modal-btn cfg-modal-btn--save"
          onClick={onSave}
          disabled={saving || !file}
        >
          {saving ? 'Saving…' : 'Save Logo'}
        </button>
      </div>

      </div>
    </div>
  );
};


export const ConfigurationPage = () => {
  const { ready } = useTranslation();

  const [configResp, setConfigResp] = useState({});
  const [configSet,  setConfigSet]  = useState(null);
  const [updated,    setUpdated]    = useState(false);
  const [logoOpen,   setLogoOpen]   = useState(false);
  const [showTerms,  setShowTerms]  = useState(false);
  const [logoKey,    setLogoKey]    = useState(0); 

  const [toggles, setToggles] = useState({
    mswipeEnabled: false,
    mandatoryBillPrintPrompt: false,
    mandatoryScheduleService: false,
    autoConfirmUplift: false,
    ensureWarehouseStockDuringUplift: false,
    msg91Enabled: false,
  });

  const { hasPermission: editSettings } = usePermissions('edit', 'configuration.settings');
  const { hasPermission: viewSettings } = usePermissions('view', 'configuration.settings');
  const [userType] = useState(storageUtil.get('userType') || '');

  React.useEffect(() => { if (viewSettings) fetchConfig(); }, [viewSettings]);
  React.useEffect(() => { if (editSettings)  setInputConfig(); }, [configResp, editSettings]);

  const setInputConfig = () => {
    setConfigSet(configResp);
    if (configResp) {
      setToggles(prev => ({
        ...prev,
        mswipeEnabled:                    !!configResp.mswipeEnabled,
        mandatoryBillPrintPrompt:         !!configResp.mandatoryBillPrintPrompt,
        mandatoryScheduleService:         !!configResp.mandatoryScheduleService,
        autoConfirmUplift:                !!configResp.autoConfirmUplift,
        ensureWarehouseStockDuringUplift: !!configResp.ensureWarehouseStockDuringUplift,
        msg91Enabled:                     !!configResp.msg91Enabled,
      }));
    }
  };

  const fetchConfig = async () => {
    try {
      const resp = await APIEndpoint.get('customer/app/config');
      if (resp.success) { setConfigSet(resp.result); setConfigResp(resp.result); }
    } catch (_) {}
  };

  const onFieldChange = e => {
    const { name, value } = e.target;
    setUpdated(true);
    setConfigSet(prev => ({ ...(prev || {}), [name]: value }));
  };

  const onToggle = name => {
    if (!editSettings) return;
    setToggles(prev => ({ ...prev, [name]: !prev[name] }));
    setUpdated(true);
  };

  const getDate = () => formatDateByTimezone(new Date(), true);

  const onSave = async () => {
    const btn = document.querySelector('.cfg-save-btn');
    if (btn) btn.disabled = true;
    const date = { startdate: getDate(), endDate: getDate() };
    const body = { ...(updated ? configSet : configResp), ...toggles, ...date };
    try {
      const resp = await APIEndpoint.post('customer/app/config', body);
      if (resp.success) { setUpdated(false); showToast(resp.message || 'Settings saved successfully'); }
    } catch (err) {
      showToast(err?.resp?.message || 'Error saving settings', true);
    } finally {
      if (btn) btn.disabled = false;
    }
  };

  const logoSrc = `${HOST_SERVER}/assets/images/public/company-logo.png?v=${logoKey}`;

  return (
    <>
      <ToastContainer />

      <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark">
        {showTerms ? (
          <>
            <div className="cfg-page-container sm-scrollbar cfg-terms-page">
              <div className="cfg-page-wrapper">

                <div className="cfg-navbar" style={{ marginBottom: '4px' }} >
                  <button type="button" className="btn btn-ghost" style={{ fontSize: '12.5px', padding: '6px 12px', marginBottom: '16px' }} onClick={() => setShowTerms(false)}>← Back to Settings</button>
                </div>

                <TermsOfUseComponent />
                
              </div>
            </div>
          </>  
        ) : (

          <div className="cfg-page-container sm-scrollbar">
            <div className="cfg-page-wrapper">

              <div className="cfg-page-header">
                <h1>Settings</h1>
                <p>Account, integration and operational preferences for this portal.</p>
              </div>

              <div className="cfg-settings-grid">

                <SettingsCard icon={<IconAccount />} title="Account & Branding" subtitle="Identity details shown across the portal and on printed documents">

                  <SettingRow label="Account Alias" desc="Internal identifier for this airline account." tooltip={toolTipInfo.accountAlias}>
                    <input type="text" name="accountAlias" className="cfg-input" value={configSet?.accountAlias ?? ''} onChange={onFieldChange} disabled={!editSettings} placeholder="e.g. akasa" />
                  </SettingRow>

                  <SettingRow label="Carrier Code" desc="2-letter IATA designator used on uplift documents and manifests.">
                    <input type="text" name="carrierCode" className="cfg-input cfg-input--mono cfg-input--sm" value={configSet?.carrierCode ?? ''} onChange={onFieldChange} disabled={!editSettings} maxLength={3} placeholder="QP" style={{ textTransform: 'uppercase' }} />
                  </SettingRow>

                  <SettingRow label="Portal Logo" desc="Displayed in the top bar and on printed bills. PNG only, max 250KB.">
                    <div
                      className="cfg-logo-drop"
                      onClick={() => userType === 'admin' && setLogoOpen(true)}
                      style={{ cursor: userType === 'admin' ? 'pointer' : 'default' }}
                      title={userType === 'admin' ? 'Click to replace logo' : ''}
                    >
                      <img
                        key={logoKey}
                        src={logoSrc}
                        alt="Portal logo"
                        className="cfg-logo-img"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                      {userType === 'admin' && (
                        <div className="cfg-logo-drop-overlay">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/></svg>
                          Replace logo
                        </div>
                      )}
                    </div>
                  </SettingRow>

                  <SettingRow label="GSTIN" desc="Goods & Services Tax identification number printed on invoices.">
                    <input type="text" name="tin" className="cfg-input cfg-input--mono" value={configSet?.tin ?? ''} onChange={onFieldChange} disabled={!editSettings} placeholder="27ABEC9580PIZC" style={{ textTransform: 'uppercase' }} />
                  </SettingRow>

                  {configSet?.timezone && (
                    <SettingRow label="Timezone" desc="All flight times, uplift windows and reports use this timezone.">
                      <input type="text" className="cfg-input" value={configSet.timezone?.formattedName ?? ''} disabled />
                    </SettingRow>
                  )}

                </SettingsCard>

                <SettingsCard icon={<IconPOS />} title="Point of Sale" subtitle="In-flight sales behaviour and billing rules">
                  <SettingRow label="mSwipe Card Payments" desc="Accept card payments through mSwipe terminals on crew devices.">
                    <MiniToggle name="mswipeEnabled" value={toggles.mswipeEnabled} onChange={onToggle} disabled={!editSettings} />
                  </SettingRow>
                  <SettingRow label="Mandatory Bill Print Prompt" desc="Force a print confirmation step before completing every sale." tooltip={toolTipInfo.mandBillPrompt}>
                    <MiniToggle name="mandatoryBillPrintPrompt" value={toggles.mandatoryBillPrintPrompt} onChange={onToggle} disabled={!editSettings} />
                  </SettingRow>
                  <SettingRow label="Schedule Service Mandatory" desc="Require a scheduled service to be active before sales can begin." tooltip={toolTipInfo.schServMand}>
                    <MiniToggle name="mandatoryScheduleService" value={toggles.mandatoryScheduleService} onChange={onToggle} disabled={!editSettings} />
                  </SettingRow>
                </SettingsCard>

                <SettingsCard icon={<IconUplift />} title="Uplift & Inventory" subtitle="Stock validation and uplift confirmation behaviour">
                  <SettingRow label="Auto-Confirm Uplift" desc="Skip manual confirmation and auto-approve uplifts at dispatch time." tooltip={toolTipInfo.autoConfUplift}>
                    <MiniToggle name="autoConfirmUplift" value={toggles.autoConfirmUplift} onChange={onToggle} disabled={!editSettings} />
                  </SettingRow>
                  <SettingRow label="Ensure Stock In Warehouse During Uplift" desc="Validate warehouse stock levels before allowing an uplift to be raised." tooltip={toolTipInfo.ensStockDuringUplift}>
                    <MiniToggle name="ensureWarehouseStockDuringUplift" value={toggles.ensureWarehouseStockDuringUplift} onChange={onToggle} disabled={!editSettings} />
                  </SettingRow>
                </SettingsCard>

                <SettingsCard icon={<IconBell />} title="Notifications & Integrations" subtitle="Third-party messaging and alert channels">
                  <SettingRow label="MSG91 SMS Gateway" desc="Send OTPs and transactional alerts to crew and customers via MSG91.">
                    <MiniToggle name="msg91Enabled" value={toggles.msg91Enabled} onChange={onToggle} disabled={!editSettings} />
                  </SettingRow>
                </SettingsCard>

              </div>

              {editSettings && (
                <div className="cfg-savebar">
                  <div className="cfg-savebar-left">
                    <span className="cfg-terms-link" onClick={() => setShowTerms(true)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && setShowTerms(true)}>
                      Terms of Use
                    </span>
                    <span className="cfg-savebar-note">Changes apply to all users of this account.</span>
                  </div>
                  <button className="cfg-save-btn" onClick={onSave}>Save Settings</button>
                </div>
              )}

            </div>
          </div>

        )}

        {logoOpen && (
          <LogoUploadModal
            hostServer={HOST_SERVER}
            onClose={() => setLogoOpen(false)}
            onSaved={() => setLogoKey(k => k + 1)}
          />
        )}

        {!ready && <div className="overlay_hide sub" />}
      </div>
    </>
  );
};
